import React, { useState, useEffect, useMemo } from 'react';
import { 
  School, Plus, Edit, Trash2, MapPin, 
  Save, X, ChevronLeft, ChevronRight, Search, 
  Loader2 
} from 'lucide-react';
import api from '../services/api';

const AdminEscolas = ({ showToast }) => {
    const [escolas, setEscolas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalAberto, setModalAberto] = useState(false);
    const [editando, setEditando] = useState(null); // Guardará o CIE original antes de qualquer edição
    const [busca, setBusca] = useState('');
    const [form, setForm] = useState({ cie: '', ua: '', nome_escola: '', endereco: '', bairro: '', lat: '', lng: '' });
    
    const [paginaAtual, setPaginaAtual] = useState(1);
    const itensPorPagina = 5;

    useEffect(() => { carregarEscolas(); }, []);

    const carregarEscolas = async () => {
        setLoading(true);
        try {
            const res = await api.get('/escolas');
            setEscolas(res.data);
        } catch (err) { 
            showToast("Erro ao carregar unidades", "error"); 
        } finally { 
            setLoading(false); 
        }
    };

    const abrirModal = (escola = null) => {
        if (escola) {
            setEditando(escola.cie);
            // Passamos os dados da escola e adicionamos explicitamente o cie_antigo para controle do backend
            setForm({ ...escola, cie_antigo: escola.cie });
        } else {
            setEditando(null);
            setForm({ cie: '', ua: '', nome_escola: '', endereco: '', bairro: '', lat: '', lng: '' });
        }
        setModalAberto(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (editando) {
                // Enviamos para a rota usando o identificador original da URL
                await api.put(`/escolas/${editando}`, form);
                showToast("Unidade atualizada!");
            } else {
                await api.post('/escolas', form);
                showToast("Unidade cadastrada!");
            }
            setModalAberto(false);
            carregarEscolas();
        } catch (err) { 
            showToast("Erro ao salvar dados", "error"); 
        } finally {
            setLoading(false);
        }
    };

    const deletarEscola = async (cie) => {
        if (!window.confirm("Atenção: Excluir a escola removerá todos os vínculos de inventário. Confirmar?")) return;
        try {
            await api.delete(`/escolas/${cie}`);
            showToast("Escola removida", "success");
            carregarEscolas();
        } catch (err) { 
            showToast("Erro ao excluir", "error"); 
        }
    };

    const escolasFiltradas = useMemo(() => {
        return escolas.filter(e => 
            e.nome_escola?.toLowerCase().includes(busca.toLowerCase()) || 
            e.cie?.toString().includes(busca)
        );
    }, [escolas, busca]);

    const totalPaginas = Math.ceil(escolasFiltradas.length / itensPorPagina) || 1;
    const dadosExibidos = escolasFiltradas.slice((paginaAtual - 1) * itensPorPagina, paginaAtual * itensPorPagina);

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="bg-slate-900 p-8 rounded-[40px] border border-slate-800 flex flex-col md:flex-row justify-between items-center gap-6 shadow-2xl">
                <div>
                    <h2 className="text-2xl font-black text-white flex items-center gap-3 tracking-tighter">
                        <School className="text-emerald-500" /> Gerenciar Unidades
                    </h2>
                    <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-1">Cadastro da Rede de Ensino</p>
                </div>
                <div className="flex gap-4 w-full md:w-auto">
                    <div className="relative flex-1 md:w-64">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                        <input className="w-full bg-slate-950 border border-slate-800 p-3 pl-12 rounded-2xl text-xs font-bold text-white outline-none focus:border-emerald-500" 
                               placeholder="BUSCAR NOME OU CIE..." value={busca} onChange={e => {setBusca(e.target.value); setPaginaAtual(1);}} />
                    </div>
                    <button onClick={() => abrirModal()} className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-4 rounded-2xl font-black text-[10px] uppercase flex items-center gap-2 transition-all active:scale-95 shadow-lg shadow-emerald-900/20">
                        <Plus size={18} /> Nova Escola
                    </button>
                </div>
            </div>

            {/* Modal com Scroll Correto */}
            {modalAberto && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-6 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 w-full max-w-3xl max-h-[90vh] rounded-[40px] shadow-2xl overflow-hidden border dark:border-slate-800 flex flex-col">
                        
                        {/* Header Modal */}
                        <div className="p-8 border-b dark:border-slate-800 flex justify-between items-center shrink-0">
                            <h3 className="text-xl font-black dark:text-white uppercase tracking-tighter italic">
                                {editando ? 'Editar Unidade' : 'Cadastrar Unidade'}
                            </h3>
                            <button onClick={() => setModalAberto(false)} className="text-slate-400 hover:text-red-500 transition-colors"><X size={24} /></button>
                        </div>

                        {/* Corpo Modal com Scroll */}
                        <form onSubmit={handleSubmit} className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6 overflow-y-auto custom-scrollbar">
                            <div className="space-y-1">
                                <label className="text-[9px] font-black text-slate-500 uppercase ml-2">CIE</label>
                                {/* REMOVIDO o disabled={editando} para permitir a edição do código CIE */}
                                <input className="w-full bg-slate-100 dark:bg-slate-950 p-4 rounded-2xl border-2 dark:border-slate-800 outline-none focus:border-emerald-500 font-bold transition-all" 
                                       value={form.cie} onChange={e => setForm({...form, cie: e.target.value})} required placeholder="000000" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[9px] font-black text-slate-500 uppercase ml-2">UA</label>
                                <input className="w-full bg-slate-100 dark:bg-slate-950 p-4 rounded-2xl border-2 dark:border-slate-800 outline-none focus:border-emerald-500 font-bold transition-all" 
                                       value={form.ua} onChange={e => setForm({...form, ua: e.target.value})} required placeholder="00.000" />
                            </div>
                            <div className="md:col-span-2 space-y-1">
                                <label className="text-[9px] font-black text-slate-500 uppercase ml-2">Nome da Escola</label>
                                <input className="w-full bg-slate-100 dark:bg-slate-950 p-4 rounded-2xl border-2 dark:border-slate-800 outline-none focus:border-emerald-500 font-bold uppercase transition-all" 
                                       value={form.nome_escola} onChange={e => setForm({...form, nome_escola: e.target.value})} required />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[9px] font-black text-slate-500 uppercase ml-2">Endereço</label>
                                <input className="w-full bg-slate-100 dark:bg-slate-950 p-4 rounded-2xl border-2 dark:border-slate-800 outline-none focus:border-emerald-500 font-bold transition-all" 
                                       value={form.endereco} onChange={e => setForm({...form, endereco: e.target.value})} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[9px] font-black text-slate-500 uppercase ml-2">Bairro</label>
                                <input className="w-full bg-slate-100 dark:bg-slate-950 p-4 rounded-2xl border-2 dark:border-slate-800 outline-none focus:border-emerald-500 font-bold uppercase transition-all" 
                                       value={form.bairro} onChange={e => setForm({...form, bairro: e.target.value})} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[9px] font-black text-slate-500 uppercase ml-2">Latitude</label>
                                <input className="w-full bg-slate-100 dark:bg-slate-950 p-4 rounded-2xl border-2 dark:border-slate-800 outline-none focus:border-emerald-500 font-bold transition-all" 
                                       value={form.lat} onChange={e => setForm({...form, lat: e.target.value})} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[9px] font-black text-slate-500 uppercase ml-2">Longitude</label>
                                <input className="w-full bg-slate-100 dark:bg-slate-950 p-4 rounded-2xl border-2 dark:border-slate-800 outline-none focus:border-emerald-500 font-bold transition-all" 
                                       value={form.lng} onChange={e => setForm({...form, lng: e.target.value})} />
                            </div>
                        </form>

                        {/* Footer Modal */}
                        <div className="p-8 border-t dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900 shrink-0 flex justify-end">
                            <button 
                                onClick={handleSubmit}
                                disabled={loading}
                                className="bg-emerald-600 text-white px-10 py-4 rounded-2xl font-black text-[10px] uppercase flex items-center gap-2 shadow-lg shadow-emerald-500/20 hover:bg-emerald-500 disabled:opacity-50 transition-all"
                            >
                                {loading ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                                Salvar Escola
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Listagem */}
            <div className="bg-white dark:bg-slate-900 rounded-[40px] border dark:border-slate-800 overflow-hidden shadow-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 dark:bg-slate-800/50">
                            <tr>
                                <th className="p-6 text-[10px] font-black text-slate-400 uppercase">CIE/UA</th>
                                <th className="p-6 text-[10px] font-black text-slate-400 uppercase">Escola</th>
                                <th className="p-6 text-[10px] font-black text-slate-400 uppercase">Localização</th>
                                <th className="p-6 text-[10px] font-black text-slate-400 uppercase text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y dark:divide-slate-800">
                            {loading && escolas.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="p-20 text-center">
                                        <Loader2 className="animate-spin mx-auto text-emerald-500 mb-4" size={40} />
                                        <p className="text-[10px] font-black text-slate-500 uppercase">Sincronizando unidades...</p>
                                    </td>
                                </tr>
                            ) : dadosExibidos.map(e => (
                                <tr key={e.cie} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                                    <td className="p-6">
                                        <div className="font-black text-emerald-500 text-xs tracking-tighter">{e.cie}</div>
                                        <div className="text-[9px] font-bold text-slate-400">UA: {e.ua}</div>
                                    </td>
                                    <td className="p-6">
                                        <div className="font-black text-sm dark:text-white uppercase tracking-tighter italic truncate max-w-xs">{e.nome_escola}</div>
                                        <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{e.bairro || 'SEM BAIRRO'}</div>
                                    </td>
                                    <td className="p-6">
                                        <div className="flex items-center gap-2 text-slate-500">
                                            <MapPin size={12} className={e.lat ? 'text-emerald-500' : 'text-slate-300'} /> 
                                            <span className="text-[10px] font-bold uppercase">{e.lat ? 'Mapeado' : 'Pendente'}</span>
                                        </div>
                                    </td>
                                    <td className="p-6 text-right space-x-2">
                                        <button onClick={() => abrirModal(e)} className="p-3 bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-emerald-500 rounded-xl transition-all shadow-sm"><Edit size={16} /></button>
                                        <button onClick={() => deletarEscola(e.cie)} className="p-3 bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-red-500 rounded-xl transition-all shadow-sm"><Trash2 size={16} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Paginação */}
                <div className="p-6 border-t dark:border-slate-800 flex justify-between items-center bg-slate-50/30 dark:bg-slate-800/20">
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Registros: {escolasFiltradas.length} | Pag. {paginaAtual} / {totalPaginas}</span>
                    <div className="flex gap-2">
                        <button disabled={paginaAtual === 1} onClick={() => setPaginaAtual(p => p - 1)} className="p-3 rounded-xl border dark:border-slate-800 disabled:opacity-20 hover:bg-white dark:hover:bg-slate-800 transition-all"><ChevronLeft size={18} /></button>
                        <button disabled={paginaAtual === totalPaginas} onClick={() => setPaginaAtual(p => p + 1)} className="p-3 rounded-xl border dark:border-slate-800 disabled:opacity-20 hover:bg-white dark:hover:bg-slate-800 transition-all"><ChevronRight size={18} /></button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminEscolas;